import { GrundartikelLink } from '@/components/catalog/GrundartikelLink'
// stahlia-frontend/src/pages/warehouse/ErstaufnahmePage.tsx
// Reiter "Erstaufnahme" (Modul ⑧, Design-Spec §6): Alt-System-Migration OrcaScan → Warehouse.
// Vier Tabs: Fortschritts-Board / Etiketten erzeugen / Picking-Abgleich / Abweichungen.
// RA6-WEB (Audit-Final, Codex gpt-5.6-sol R5): Gate-Matrix folgt exakt den Backend-Routen
// (read = general, place-empty/discrepancy = count, generate/release/confirm/reset/correct/
// abgleich = lead); Abgleich-Commit + Etiketten-Generierung idempotent mit Snapshot-Bindung.
import { useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import { useAuth } from '@/api/auth'
import { isTransportError } from '@/api/client'
import { newIdempotencyKey } from '@/lib/offline-queue'
import {
  useMigrationProgress, useAbweichungen,
  useGenerateLabels, useReleaseRow, useAbgleichPreview, useAbgleichCommit,
  useConfirmEmptyPlaces, openVorbelegtLabelsPdf, openShelfOverviewPdf, type AbgleichRef,
  useLabelBatches, useCancelBatch, useShelfOverview, usePlaceActions,
  type ShelfCell, type LabelBatch,
} from '@/api/warehouseMigration'
import { useJournal, useUnlinked } from '@/api/warehouse'
import { progressPercent, rowStatusColor, rowStatusLabel, getLagerForRow } from '@/lib/migration-worklist'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import RegalFront from '@/components/warehouse/RegalFront'

export default function ErstaufnahmePage() {
  const { warehouse } = useAuth()
  const unlinked = useUnlinked()
  const [activeTab, setActiveTab] = useState('board')
  // R7-Nachschliff: die Release-Unklar-Sperre lebt auf PAGE-Ebene — im BoardTab ging sie
  // beim Tab-Wechsel (unmount) verloren und der explizite Pruefschritt war umgehbar.
  const [unclearRelease, setUnclearRelease] = useState<Set<string>>(new Set())
  const [scope, setScope] = useState<'lager1' | 'lager2' | 'all'>(() => {
    const saved = localStorage.getItem('erstaufnahme_scope')
    if (saved === 'lager1' || saved === 'lager2' || saved === 'all') return saved
    return 'lager1'
  })
  const [selectedRowForLabels, setSelectedRowForLabels] = useState<string>('')
  const [selectedCountForLabels, setSelectedCountForLabels] = useState<number>(30)

  const handleScopeChange = (nextScope: 'lager1' | 'lager2' | 'all') => {
    setScope(nextScope)
    localStorage.setItem('erstaufnahme_scope', nextScope)
  }

  const unlinkedCount = unlinked.data?.length ?? 0

  return (
    <div className="p-4 space-y-4 max-w-5xl mx-auto">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-xl font-bold">Erstaufnahme</h1>
        <div className="text-sm text-muted">Alt-System-Migration (OrcaScan → Warehouse-Modul)</div>
      </div>

      {unlinkedCount > 0 && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 flex items-center justify-between gap-3 text-amber-900 dark:text-amber-200">
          <div className="flex items-center gap-2">
            <span className="text-base" aria-hidden>⚠️</span>
            <span className="text-sm font-medium">
              {unlinkedCount} Palette(n) ohne SKU-Zuordnung (nicht-katalogisierte Sternnummern o.ä.) im Alt-Bestand.
            </span>
          </div>
          <a
            href="/warehouse/leiter"
            className="text-xs font-semibold underline hover:text-amber-800 dark:hover:text-amber-100 whitespace-nowrap"
          >
            Im Leiter-Board zuordnen →
          </a>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="board">Fortschritts-Board</TabsTrigger>
          <TabsTrigger value="etiketten">Etiketten erzeugen</TabsTrigger>
          <TabsTrigger value="abgleich">Picking-Abgleich</TabsTrigger>
          <TabsTrigger value="abweichungen">Abweichungen</TabsTrigger>
        </TabsList>
        <TabsContent value="board">
          <BoardTab
            count={warehouse.count}
            lead={warehouse.lead}
            unclearRelease={unclearRelease}
            setUnclearRelease={setUnclearRelease}
            scope={scope}
            setScope={handleScopeChange}
            onSelectRowForLabels={(rowCode, offen) => {
              setSelectedRowForLabels(rowCode)
              setSelectedCountForLabels(Math.min(500, Math.max(1, offen || 30)))
              setActiveTab('etiketten')
            }}
          />
        </TabsContent>
        <TabsContent value="etiketten">
          <EtikettenTab
            lead={warehouse.lead}
            initialRowCode={selectedRowForLabels}
            initialCount={selectedCountForLabels}
          />
        </TabsContent>
        <TabsContent value="abgleich"><AbgleichTab lead={warehouse.lead} /></TabsContent>
        <TabsContent value="abweichungen"><AbweichungenTab scope={scope} setScope={handleScopeChange} /></TabsContent>
      </Tabs>
    </div>
  )
}

// ---------- ① Fortschritts-Board ----------

function BoardTab({ count, lead, unclearRelease, setUnclearRelease, scope, setScope, onSelectRowForLabels }: {
  count: boolean; lead: boolean
  // R6-Nachschliff (RA6-WEB-6) + R7: Freigabe mit unklarem Ausgang (Transportfehler) sperrt
  // den Button dieses Regals, bis der Nutzer den aktualisierten Board-Stand explizit
  // bestaetigt — der Zustand lebt auf PAGE-Ebene, damit ein Tab-Wechsel ihn nicht loescht.
  unclearRelease: Set<string>; setUnclearRelease: React.Dispatch<React.SetStateAction<Set<string>>>
  scope: 'lager1' | 'lager2' | 'all'
  setScope: (s: 'lager1' | 'lager2' | 'all') => void
  onSelectRowForLabels: (rowCode: string, offen: number) => void
}) {
  const progress = useMigrationProgress()
  const release = useReleaseRow()
  const confirmEmpty = useConfirmEmptyPlaces()
  const [openRows, setOpenRows] = useState<Set<string>>(new Set())
  const [controlRow, setControlRow] = useState<string | null>(null)
  // RA6-WEB (Minor, R6-Nachschliff): PDF-Busy als SET je Regal — ein einzelner Wert wurde
  // von parallelen PDFs verschiedener Regale ueberschrieben und vorzeitig genullt.
  const [pdfBusyRows, setPdfBusyRows] = useState<Set<string>>(new Set())
  const setPdfBusy = (rc: string, busy: boolean) => setPdfBusyRows((s) => {
    const n = new Set(s); if (busy) n.add(rc); else n.delete(rc); return n
  })

  function toggleOpen(rc: string) {
    setOpenRows((s) => {
      const n = new Set(s); if (n.has(rc)) n.delete(rc); else n.add(rc); return n
    })
  }

  async function onRelease(rowCode: string) {
    try {
      await release.mutateAsync(rowCode)
      toast.success(`Regal ${rowCode} freigegeben`)
    } catch (e) {
      // useReleaseRow().onError hat den Toast + Refresh uebernommen; hier nur die Sperre.
      if (isTransportError(e)) setUnclearRelease((s) => new Set(s).add(rowCode))
    }
  }

  async function onConfirmEmpty(rowCode: string) {
    // Leiter-Aktion ohne Scanner: setzt alle laut Alt-Liste leeren Plätze der Reihe auf „leer".
    // Bewusster Bestätigungsdialog — Status-Änderung an mehreren Plätzen.
    if (!window.confirm(
      `Regal ${rowCode}: Alle laut Alt-Liste leeren Plätze als „leer" bestätigen?\n\n` +
      `Nutze zuvor die gedruckte Übersicht zur Kontrolle vor Ort. Belegte Plätze bleiben offen ` +
      `(sie brauchen weiterhin ein Etikett + Erfassung).`,
    )) return
    try {
      const r = await confirmEmpty.mutateAsync(rowCode)
      toast.success(
        `Regal ${rowCode}: ${r.confirmed} Leerplätze bestätigt`
        + (r.skipped_occupied > 0 ? ` — ${r.skipped_occupied} übersprungen (im System belegt, bleiben offen)` : ''),
      )
    } catch { /* Fehler-Toast kommt aus useConfirmEmptyPlaces().onError */ }
  }

  const totals = progress.data?.totals
  const allRows = progress.data?.rows ?? []
  const offenByRow = progress.data?.offenByRow ?? {}

  const rows = useMemo(() => {
    if (scope === 'lager1') return allRows.filter((r) => getLagerForRow(r.rowCode) === 1)
    if (scope === 'lager2') return allRows.filter((r) => getLagerForRow(r.rowCode) === 2)
    return allRows
  }, [allRows, scope])

  const displayedTotals = useMemo(() => {
    if (scope === 'all' && totals) return totals
    return rows.reduce(
      (acc, r) => ({
        total: acc.total + r.total,
        aufgenommen: acc.aufgenommen + r.aufgenommen,
        leer: acc.leer + r.leer,
        abweichung: acc.abweichung + r.abweichung,
        offen: acc.offen + r.offen,
      }),
      { total: 0, aufgenommen: 0, leer: 0, abweichung: 0, offen: 0 },
    )
  }, [rows, scope, totals])

  const pct = displayedTotals.total > 0 ? progressPercent(displayedTotals) : 0

  return (
    <div className="space-y-4 pt-4">
      {/* Scope-Filter: Lager 1 vs. Lager 2 vs. Alle */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="inline-flex rounded-lg border border-line bg-panel p-1 text-xs">
          <button
            type="button"
            onClick={() => setScope('lager1')}
            className={'px-3 py-1.5 rounded-md font-medium transition-all ' + (scope === 'lager1' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted hover:text-foreground')}
          >
            Lager 1 (R01–R21)
          </button>
          <button
            type="button"
            onClick={() => setScope('lager2')}
            className={'px-3 py-1.5 rounded-md font-medium transition-all ' + (scope === 'lager2' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted hover:text-foreground')}
          >
            Lager 2 (R22–R32)
          </button>
          <button
            type="button"
            onClick={() => setScope('all')}
            className={'px-3 py-1.5 rounded-md font-medium transition-all ' + (scope === 'all' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted hover:text-foreground')}
          >
            Alle Regale
          </button>
        </div>
        <div className="text-xs text-muted font-mono">
          {scope === 'lager1' ? 'Halle 1 · Palettenregale R01–R21' : scope === 'lager2' ? 'Halle 2 · Palettenregale R22–R32' : 'Alle Regale (Halle 1 + Halle 2)'}
        </div>
      </div>

      <div className="rounded-xl border border-line bg-panel p-4 space-y-2">
        <div className="flex items-center justify-between">
          <span className="font-semibold">
            {scope === 'lager1' ? 'Fortschritt Lager 1' : scope === 'lager2' ? 'Fortschritt Lager 2' : 'Gesamtfortschritt'}
          </span>
          <span className="text-sm font-mono">{pct}% · {displayedTotals.offen} offen von {displayedTotals.total} Plätzen</span>
        </div>
        <div className="h-2 rounded-full bg-panel-2 overflow-hidden">
          <div className="h-full bg-[#108474] transition-all" style={{ width: `${pct}%` }} />
        </div>
      </div>

      <div className="space-y-2">
        {rows.map((r) => {
          const color = rowStatusColor(r.complete, r.offen, r.total)
          const rowPct = progressPercent(r)
          const barColor = color === 'green' ? 'bg-green-600' : color === 'amber' ? 'bg-[#FFB300]' : 'bg-zinc-300'
          const offenPlaces = offenByRow[r.rowCode] ?? []
          const isOpen = openRows.has(r.rowCode)
          return (
            <div key={r.rowCode} className="space-y-2">
            <div className="rounded-xl border border-line bg-panel p-3 space-y-2">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <span
                    className={
                      'inline-block size-2.5 rounded-full ' +
                      (color === 'green' ? 'bg-green-600' : color === 'amber' ? 'bg-[#FFB300]' : 'bg-zinc-300')
                    }
                    aria-hidden
                  />
                  <span className="font-mono font-semibold">{r.rowCode}</span>
                  <span className="text-sm text-muted">{rowStatusLabel(color)}</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-muted">
                  <span>{r.aufgenommen} aufgenommen</span>
                  <span>{r.leer} leer</span>
                  {r.abweichung > 0 && <span className="text-red-600 font-medium">{r.abweichung} Abweichung(en)</span>}
                  {r.offen > 0 && (
                    <button type="button" className="underline text-primary" onClick={() => toggleOpen(r.rowCode)}>
                      {r.offen} offen {isOpen ? '▲' : '▼'}
                    </button>
                  )}
                  <Button
                    size="sm" variant="outline" disabled={pdfBusyRows.has(r.rowCode)}
                    onClick={() => {
                      setPdfBusy(r.rowCode, true)
                      openShelfOverviewPdf(r.rowCode)
                        .catch((e) => toast.error(e instanceof Error ? e.message : 'PDF fehlgeschlagen'))
                        .finally(() => setPdfBusy(r.rowCode, false))
                    }}
                  >
                    {pdfBusyRows.has(r.rowCode) ? 'PDF…' : 'Übersicht'}
                  </Button>
                  {/* RA6-WEB-4: Raster-ANSICHT ist read (Backend: overview = warehouse:read) —
                      fuer alle sichtbar; die Aktionen darin sind count/lead-gated. */}
                  <Button
                    size="sm" variant={controlRow === r.rowCode ? 'default' : 'outline'}
                    onClick={() => setControlRow(controlRow === r.rowCode ? null : r.rowCode)}
                  >
                    Digital kontrollieren
                  </Button>
                  {lead && r.offen > 0 && (
                    <Button
                      size="sm" variant="outline"
                      onClick={() => onSelectRowForLabels(r.rowCode, r.offen)}
                      title={`Etiketten für Regal ${r.rowCode} erzeugen`}
                    >
                      Etiketten ({r.offen})
                    </Button>
                  )}
                  {lead && (
                    <Button
                      size="sm" variant="outline"
                      disabled={r.leer === 0 && r.offen === 0 ? true : confirmEmpty.isPending}
                      onClick={() => void onConfirmEmpty(r.rowCode)}
                    >
                      Leerplätze bestätigen
                    </Button>
                  )}
                  {lead && !unclearRelease.has(r.rowCode) && (
                    <Button
                      size="sm" variant="outline"
                      disabled={r.offen > 0 || release.isPending}
                      onClick={() => void onRelease(r.rowCode)}
                    >
                      Regal freigeben
                    </Button>
                  )}
                  {lead && unclearRelease.has(r.rowCode) && (
                    <Button
                      size="sm" variant="destructive"
                      onClick={() => {
                        // expliziter Bestaetigungs-Schritt nach unklarem Ausgang: Sperre
                        // aufheben — der Leiter hat den aktualisierten Board-Stand gesehen.
                        setUnclearRelease((s) => { const n = new Set(s); n.delete(r.rowCode); return n })
                        toast.info(`Regal ${r.rowCode}: Sperre aufgehoben — Stand geprüft, Freigabe kann bei Bedarf wiederholt werden`)
                      }}
                    >
                      Status unklar — geprüft, entsperren
                    </Button>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-2 rounded-full bg-zinc-200 dark:bg-zinc-800 overflow-hidden">
                  <div className={'h-full transition-all ' + barColor} style={{ width: `${rowPct}%` }} />
                </div>
                <span className="text-xs font-mono text-muted w-10 text-right">{rowPct}%</span>
              </div>
              {isOpen && offenPlaces.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {offenPlaces.map((p) => (
                    <span key={p} className="px-2 py-0.5 rounded bg-panel-2 text-xs font-mono">{p}</span>
                  ))}
                </div>
              )}
            </div>
            {controlRow === r.rowCode && <ShelfControlGrid rowCode={r.rowCode} count={count} lead={lead} onClose={() => setControlRow(null)} />}
            </div>
          )
        })}
        {rows.length === 0 && !progress.isLoading && (
          <div className="p-4 text-center text-muted rounded-xl border border-line">
            Keine Worklist-Einträge — Alt-CSV-Import noch ausstehend.
          </div>
        )}
      </div>

      <MigrationActivityFeed />
    </div>
  )
}

// Aktivitäts-Feed aus dem Journal (Spec §6①). `useJournal` filtert serverseitig nur nach einer
// einzelnen `action` (exact match, src/modules/warehouse/types.ts journalQuerySchema) — für die
// drei Erstaufnahme-Aktionen wird daher ungefiltert das jüngste Fenster geholt und clientseitig
// auf die relevanten Actions eingeschränkt (Display-only, keine neue Backend-Logik).
const MIGRATION_ACTIONS = new Set(['erstaufnahme', 'alt_picking_korrektur', 'blank_batch_created'])
const MIGRATION_FEED_LIMIT = 15

function MigrationActivityFeed() {
  const journal = useJournal({ limit: '50' })
  const entries = (journal.data ?? [])
    .filter((m) => MIGRATION_ACTIONS.has(String(m['action'])))
    .slice(0, MIGRATION_FEED_LIMIT)

  return (
    <div className="rounded-xl border border-line bg-panel p-3 space-y-2">
      <div className="font-semibold text-sm">Aktivitäts-Feed (Erstaufnahme-Journal)</div>
      <div className="space-y-1">
        {entries.map((m) => (
          <div key={String(m['id'])} className="flex items-start gap-2 text-xs border-t border-line pt-1 first:border-t-0 first:pt-0">
            <span className="whitespace-nowrap text-muted font-mono">
              {new Date(String(m['createdAt'])).toLocaleString('de-DE')}
            </span>
            <span className="font-mono text-muted">{String(m['action'])}</span>
            <span className="flex-1 truncate">{String(m['note'] ?? '—')}</span>
            <span className="whitespace-nowrap text-muted">{String(m['actorName'] ?? '—')}</span>
          </div>
        ))}
        {entries.length === 0 && !journal.isLoading && (
          <div className="p-2 text-center text-muted">Noch keine Erstaufnahme-Aktivitäten.</div>
        )}
      </div>
    </div>
  )
}

// ---------- ② Etiketten erzeugen ----------

function EtikettenTab({
  lead,
  initialRowCode,
  initialCount,
}: {
  lead: boolean
  initialRowCode?: string
  initialCount?: number
}) {
  const gen = useGenerateLabels()
  const progress = useMigrationProgress()
  const allRows = progress.data?.rows ?? []

  const [rowCode, setRowCode] = useState(initialRowCode ?? '')
  const [count, setCount] = useState(initialCount ?? 30)

  useEffect(() => {
    if (initialRowCode) {
      setRowCode(initialRowCode)
    }
  }, [initialRowCode])

  useEffect(() => {
    if (initialCount && initialCount > 0) {
      setCount(initialCount)
    }
  }, [initialCount])

  // RA6-WEB-3: Vorgangs-Key + synchroner Single-Flight. Bei ambivalentem Transportfehler
  // bleibt der Key stehen (pendingGen) — "Erneut versuchen" sendet DENSELBEN Key, das
  // Backend replayed denselben Batch statt die naechsten N Plaetze zu binden.
  const genBusyRef = useRef(false)
  const [pendingGen, setPendingGen] = useState<{ key: string; rowCode: string; count: number } | null>(null)

  async function runGen(rc: string, cnt: number, key: string) {
    if (genBusyRef.current) return
    genBusyRef.current = true
    try {
      const r = await gen.mutateAsync({ row_code: rc, count: cnt, idempotency_key: key })
      setPendingGen(null)
      if (r.paletteNos.length === 0) toast.error(`Regal ${rc}: keine offenen belegten Plätze — entweder alle bereits etikettiert/leer oder für dieses Regal keine Alt-Zeilen importiert.`)
      else toast.success(`${r.paletteNos.length} Etikett(en) für Regal ${rc} ${r.duplicate ? 'bestätigt (war bereits erzeugt)' : 'erzeugt'} — als Druckauftrag unten`)
    } catch (e) {
      if (isTransportError(e)) {
        setPendingGen({ key, rowCode: rc, count: cnt })
        toast.warning('Verbindung unklar — der Auftrag wurde möglicherweise erzeugt. „Erneut versuchen" prüft mit demselben Vorgang nach.')
      } else {
        setPendingGen(null)
        toast.error(e instanceof Error ? e.message : 'Erzeugen fehlgeschlagen')
      }
    } finally {
      genBusyRef.current = false
    }
  }

  async function onGen() {
    const rc = rowCode.trim().toUpperCase()
    if (!rc) { toast.error('Regal auswählen (z. B. R01)'); return }
    if (pendingGen) return // erst den offenen Vorgang klaeren ("Erneut versuchen")
    await runGen(rc, count, newIdempotencyKey())
  }

  return (
    <div className="space-y-4 pt-4">
      {/* RA6-WEB-4: die Druckauftragsliste ist read (Backend: batches = warehouse:read) und
          bleibt fuer Nicht-Leiter sichtbar — nur das ERZEUGEN ist lead-gated. */}
      {lead ? (
        <div className="rounded-xl border border-line bg-panel p-4 space-y-3">
          <div className="font-semibold">Etiketten aus Alt-Liste erzeugen</div>
          <p className="text-sm text-muted">
            Es werden nur für <span className="font-medium">belegte</span> Plätze Etiketten erzeugt
            (leere Plätze <span className="font-mono">*0</span>/<span className="font-mono">*</span> werden übersprungen).
            Leere Plätze laufen über die <span className="font-medium">Regal-Übersicht</span> bzw. die
            digitale Kontrolle im Fortschritts-Board.
          </p>
          <div className="flex gap-2 items-end flex-wrap">
            <label className="text-sm">Regal
              <select
                className="mt-1 block w-56 rounded border border-line bg-panel px-2 py-1.5 font-mono text-sm"
                value={rowCode}
                disabled={!!pendingGen}
                onChange={(e) => {
                  const rc = e.target.value
                  setRowCode(rc)
                  const found = allRows.find((r) => r.rowCode === rc)
                  if (found) {
                    setCount(Math.min(500, Math.max(1, found.offen || 30)))
                  }
                }}
              >
                <option value="">-- Regal wählen --</option>
                {allRows.filter((r) => getLagerForRow(r.rowCode) === 1).length > 0 && (
                  <optgroup label="Lager 1 (R01–R21)">
                    {allRows.filter((r) => getLagerForRow(r.rowCode) === 1).map((r) => (
                      <option key={r.rowCode} value={r.rowCode}>
                        {r.rowCode} ({r.offen} offen{r.offen === 0 ? ' ✓ fertig' : ''})
                      </option>
                    ))}
                  </optgroup>
                )}
                {allRows.filter((r) => getLagerForRow(r.rowCode) === 2).length > 0 && (
                  <optgroup label="Lager 2 (R22–R32)">
                    {allRows.filter((r) => getLagerForRow(r.rowCode) === 2).map((r) => (
                      <option key={r.rowCode} value={r.rowCode}>
                        {r.rowCode} ({r.offen} offen{r.offen === 0 ? ' ✓ fertig' : ''})
                      </option>
                    ))}
                  </optgroup>
                )}
                {allRows.filter((r) => getLagerForRow(r.rowCode) === null).length > 0 && (
                  <optgroup label="Sonstige Regale">
                    {allRows.filter((r) => getLagerForRow(r.rowCode) === null).map((r) => (
                      <option key={r.rowCode} value={r.rowCode}>
                        {r.rowCode} ({r.offen} offen)
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
            </label>
            <label className="text-sm">Anzahl
              <input type="number" min={1} max={500} className="mt-1 block w-24 rounded border border-line bg-panel px-2 py-1.5" value={count} disabled={!!pendingGen} onChange={(e) => setCount(Number(e.target.value))} />
            </label>
            {!pendingGen && (
              <Button onClick={() => void onGen()} disabled={gen.isPending || !rowCode}>{gen.isPending ? 'Erzeuge…' : 'Erzeugen'}</Button>
            )}
            {pendingGen && (
              <Button variant="destructive" disabled={gen.isPending}
                onClick={() => void runGen(pendingGen.rowCode, pendingGen.count, pendingGen.key)}>
                {gen.isPending ? 'Prüfe…' : `Erneut versuchen (${pendingGen.rowCode}, ${pendingGen.count})`}
              </Button>
            )}
          </div>
          {pendingGen && (
            <p className="text-sm text-amber-600">
              Offener Vorgang: Die letzte Erzeugung für <span className="font-mono">{pendingGen.rowCode}</span> ist unbestätigt
              (Verbindungsproblem). Erst klären, bevor ein neuer Auftrag gestartet wird.
            </p>
          )}
        </div>
      ) : (
        <div className="text-sm text-muted">Etiketten erzeugen ist der Lagerleitung vorbehalten — Druckaufträge unten sind einsehbar.</div>
      )}
      <BatchListPanel lead={lead} />
    </div>
  )
}

// Persistente Druckaufträge (Migration 0081): bleiben nach Tab-Wechsel/Reload sichtbar, jederzeit
// nachdruckbar, einzeln stornierbar. Stornierte bleiben als Audit-Zeile grau in der Liste.
function BatchListPanel({ lead }: { lead: boolean }) {
  const batches = useLabelBatches()
  const cancel = useCancelBatch()
  const rows = batches.data ?? []
  // R6-Nachschliff (Minor): Busy-Set je (Batch, PDF-Art) — Nachdruck hatte keinen Guard.
  const [pdfBusy, setPdfBusyKeys] = useState<Set<string>>(new Set())
  const runPdf = (key: string, fn: () => Promise<void>) => {
    if (pdfBusy.has(key)) return
    setPdfBusyKeys((s) => new Set(s).add(key))
    fn()
      .catch((e) => toast.error(e instanceof Error ? e.message : 'PDF fehlgeschlagen'))
      .finally(() => setPdfBusyKeys((s) => { const n = new Set(s); n.delete(key); return n }))
  }

  async function onCancel(b: LabelBatch) {
    if (!window.confirm(
      `Druckauftrag #${b.id} (Regal ${b.rowCode}, ${b.labelCount} Etiketten) stornieren?\n\n` +
      `Die noch nicht erfassten Etiketten werden gelöscht und die Plätze wieder generierbar. ` +
      `Der Auftrag bleibt als „storniert" sichtbar.`,
    )) return
    try {
      const r = await cancel.mutateAsync(b.id)
      toast.success(`Auftrag #${b.id} storniert (${r.freed} Etiketten gelöscht)`)
    } catch { /* onError */ }
  }

  return (
    <div className="rounded-xl border border-line bg-panel p-4 space-y-2">
      <div className="font-semibold">Druckaufträge</div>
      {rows.length === 0 && !batches.isLoading && <div className="text-sm text-muted">Noch keine Etiketten erzeugt.</div>}
      <div className="space-y-1">
        {rows.map((b) => {
          const cancelled = b.status === 'cancelled'
          // Erfassungs-Kontrolle (User-Wunsch 2026-07-15): wie viele der erzeugten Etiketten
          // sind schon per Scan-App gebucht? Komplett = grün, teilweise = amber, 0 = grau.
          const captured = b.capturedCount ?? 0
          const capturedCls = captured >= b.labelCount && b.labelCount > 0
            ? 'text-green-700 dark:text-green-500 font-semibold'
            : captured > 0 ? 'text-amber-600 font-semibold' : 'text-muted'
          return (
            <div key={b.id} className={'flex items-center justify-between gap-2 flex-wrap border-t border-line pt-2 first:border-t-0 first:pt-0 ' + (cancelled ? 'opacity-60' : '')}>
              <div className="text-sm">
                <span className="font-mono font-semibold">#{b.id}</span> · Regal <span className="font-mono">{b.rowCode}</span> · {b.labelCount} Etikett(en)
                <span className={capturedCls}> · {captured}/{b.labelCount} erfasst{captured >= b.labelCount && b.labelCount > 0 ? ' ✓' : ''}</span>
                {b.paletteNos.length > 0 && <span className="font-mono text-muted"> · {b.paletteNos[0]} … {b.paletteNos[b.paletteNos.length - 1]}</span>}
                {b.createdAt && <span className="text-muted"> · {new Date(b.createdAt).toLocaleString('de-DE')}</span>}
                {cancelled && <span className="text-red-600"> · storniert{b.cancelledByName ? ` von ${b.cancelledByName}` : ''}</span>}
              </div>
              {/* Nachdruck bleibt auch bei STORNIERTEN Auftraegen moeglich, solange erfasste
                  (booked) Etiketten ueberlebt haben (User-Wunsch 2026-07-15) — paletteNos
                  enthaelt bei cancelled genau diese Ueberlebenden. Nur Stornieren verschwindet. */}
              {b.paletteNos.length > 0 && (
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" disabled={pdfBusy.has(`${b.id}:front`)} onClick={() => runPdf(`${b.id}:front`, () => openVorbelegtLabelsPdf('front', b.paletteNos))}>{pdfBusy.has(`${b.id}:front`) ? 'PDF…' : 'Front'}</Button>
                  <Button size="sm" variant="outline" disabled={pdfBusy.has(`${b.id}:side`)} onClick={() => runPdf(`${b.id}:side`, () => openVorbelegtLabelsPdf('side', b.paletteNos))}>{pdfBusy.has(`${b.id}:side`) ? 'PDF…' : 'Seiten'}</Button>
                  {!cancelled && lead && <Button size="sm" variant="outline" disabled={cancel.isPending} onClick={() => void onCancel(b)}>Stornieren</Button>}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ---------- ③ Picking-Abgleich ----------

function AbgleichTab({ lead }: { lead: boolean }) {
  const preview = useAbgleichPreview()
  const commit = useAbgleichCommit()
  const [raw, setRaw] = useState('')
  // R7-Nachschliff: rawRef spiegelt den AKTUELLEN Textarea-Wert fuer den Snapshot-Match in
  // der Preview-Antwort — die State-Variable `raw` in der Handler-Closure ist der Wert zum
  // RENDER-Zeitpunkt und erkennt eine Aenderung zwischen Request-Start und Disabled-Render
  // nicht (Stale-Closure; der Vergleich war wirkungslos).
  const rawRef = useRef('')
  const [result, setResult] = useState<{ refs: AbgleichRef[]; unmatched: string[] } | null>(null)
  // RA6-WEB-1: Vorschau und Commit sind an DENSELBEN Eingabe-Snapshot gebunden — committet
  // wird immer previewedRaw (der geprüfte Text), nie der live editierbare State; jede
  // Textänderung nach der Vorschau verwirft das Ergebnis (neue Vorschau nötig).
  const [previewedRaw, setPreviewedRaw] = useState('')
  // RA6-WEB-2: Commit-Key gehört zum Vorschau-Snapshot. Ambivalenter Transportfehler ⇒
  // Eingabe gesperrt (pendingCommit), „Erneut senden" wiederholt mit DEMSELBEN Key —
  // das Backend replayed das Original-Ergebnis (Migration 0085) statt neu zu buchen.
  const commitKeyRef = useRef<string | null>(null)
  const commitBusyRef = useRef(false)
  const [pendingCommit, setPendingCommit] = useState<{ key: string; raw: string } | null>(null)

  // R6-Nachschliff: waehrend Vorschau ODER Commit in-flight sind, ist die Eingabe gesperrt
  // (Textarea disabled unten) und kein zweiter Einstieg moeglich — sonst konnte eine
  // verspaetete Vorschau-Antwort A ein inzwischen auf B geaendertes Textfeld "beglaubigen".
  const previewBusyRef = useRef(false)
  async function onPreview() {
    if (pendingCommit || previewBusyRef.current || commitBusyRef.current) return
    if (!raw.trim()) { toast.error('Picking-Liste ist leer'); return }
    previewBusyRef.current = true
    const requestedRaw = raw
    try {
      const r = await preview.mutateAsync(requestedRaw)
      // Antwort nur uebernehmen, wenn der AKTUELLE Text (rawRef, nicht die Render-Closure)
      // noch dem angefragten Snapshot entspricht (Sicherheitsnetz zusaetzlich zur
      // disabled-Textarea).
      if (rawRef.current !== requestedRaw) return
      setResult(r)
      setPreviewedRaw(requestedRaw)
      commitKeyRef.current = newIdempotencyKey()
    } catch { /* Fehler-Toast kommt aus useAbgleichPreview().onError */ } finally {
      previewBusyRef.current = false
    }
  }

  async function runCommit(rawInput: string, key: string) {
    if (commitBusyRef.current) return
    commitBusyRef.current = true
    try {
      const r = await commit.mutateAsync({ raw_input: rawInput, idempotency_key: key })
      toast.success(
        `${r.updated} Palette(n) als angebrochen markiert` +
        (r.unmatched > 0 ? ` · ${r.unmatched} Klärfall/-fälle im Journal` : '') +
        (r.duplicate ? ' (bereits gebucht — Wiederholung erkannt)' : ''),
      )
      setResult(null)
      setRaw('')
      setPreviewedRaw('')
      commitKeyRef.current = null
      setPendingCommit(null)
    } catch (e) {
      if (isTransportError(e)) {
        setPendingCommit({ key, raw: rawInput })
        toast.warning('Verbindung unklar — die Buchung ist möglicherweise durch. „Erneut senden" prüft mit demselben Vorgang nach (keine Doppelbuchung).')
      } else {
        // klare fachliche Ablehnung: Vorgang beendet, naechster Versuch = neue Handlung.
        // R6-Nachschliff: Ergebnis + Snapshot MIT verwerfen — sonst bliebe der
        // "N buchen"-Button sichtbar, dessen Klick ohne Key wirkungslos waere.
        commitKeyRef.current = null
        setResult(null)
        setPreviewedRaw('')
        toast.error(e instanceof Error ? e.message : 'Buchung fehlgeschlagen')
      }
    } finally {
      commitBusyRef.current = false
    }
  }

  async function onCommit() {
    if (!result || !commitKeyRef.current) return
    if (result.refs.filter((r) => r.found).length === 0) {
      toast.error('Keine zuordenbaren Paletten in der Vorschau'); return
    }
    await runCommit(previewedRaw, commitKeyRef.current)
  }

  if (!lead) {
    return <div className="pt-4 text-muted">Der Picking-Abgleich ist ausschließlich der Lagerleitung vorbehalten.</div>
  }

  const foundCount = result?.refs.filter((r) => r.found).length ?? 0

  return (
    <div className="space-y-4 pt-4">
      <div className="rounded-xl border border-line bg-panel p-4 space-y-3">
        <div className="font-semibold">Alt-Picking-Liste einfügen</div>
        <p className="text-sm text-muted">
          Wöchentliche OrcaScan-Liste (CSV) direkt einfügen — gematcht wird über den Lagerplatz
          (Spalte <span className="font-mono">ARTIKEL</span>); die alte Scan-ID (Spalte
          <span className="font-mono"> Barcode</span>) wird ignoriert. Kopfzeile ist erlaubt.
          Bei mehreren Paletten an einem Platz wird die FIFO-älteste verschlossene markiert.
        </p>
        <Textarea
          className="w-full h-32 font-mono text-xs"
          value={raw}
          disabled={!!pendingCommit || preview.isPending || commit.isPending}
          onChange={(e) => {
            setRaw(e.target.value)
            rawRef.current = e.target.value  // R7: aktueller Wert fuer den Snapshot-Match
            // RA6-WEB-1: Text weicht von der geprüften Vorschau ab ⇒ Vorschau + Key verfallen.
            if (result && e.target.value !== previewedRaw) {
              setResult(null)
              commitKeyRef.current = null
            }
          }}
          placeholder={'Barcode,ARTIKEL,Quantity,INFO,Date\n*1685,R01+F13+P2+EB6,1,,07/07/2026 05:27:25'}
        />
        <div className="flex gap-2 items-center flex-wrap">
          {!pendingCommit && (
            <Button variant="outline" onClick={() => void onPreview()} disabled={preview.isPending}>
              {preview.isPending ? 'Prüfe…' : 'Vorschau'}
            </Button>
          )}
          {result && !pendingCommit && (
            <Button onClick={() => void onCommit()} disabled={commit.isPending || foundCount === 0}>
              {commit.isPending ? 'Buche…' : `${foundCount} Palette(n) buchen`}
            </Button>
          )}
          {pendingCommit && (
            <Button variant="destructive" disabled={commit.isPending}
              onClick={() => void runCommit(pendingCommit.raw, pendingCommit.key)}>
              {commit.isPending ? 'Prüfe…' : 'Erneut senden (gleicher Vorgang)'}
            </Button>
          )}
        </div>
        {pendingCommit && (
          <p className="text-sm text-amber-600">
            Offener Buchungsvorgang mit unklarem Ausgang (Verbindungsproblem) — die Eingabe ist gesperrt,
            bis „Erneut senden" das Ergebnis bestätigt hat. Es wird nichts doppelt gebucht.
          </p>
        )}
      </div>

      {result && (
        <div className="overflow-x-auto rounded-xl border border-line">
          <table className="w-full text-sm">
            <thead className="bg-panel text-left">
              <tr>
                <th className="p-2">Platz (Liste)</th><th className="p-2">Paletten-Nr.</th>
                <th className="p-2">Match</th><th className="p-2">Aktueller Zustand</th><th className="p-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {result.refs.map((r, i) => (
                <tr key={`${r.raw}-${i}`} className="border-t border-line">
                  <td className="p-2 font-mono">{r.placeBarcode ?? '—'}</td>
                  <td className="p-2 font-mono text-muted">{r.paletteNo ?? '—'}</td>
                  <td className="p-2 text-muted">
                    {r.matchedBy === 'place'
                      ? <>Platz{r.candidateCount > 1 ? ` · ${r.candidateCount} Paletten (FIFO-älteste)` : ''}</>
                      : r.matchedBy === 'palette' ? 'Paletten-Nr.' : '—'}
                  </td>
                  <td className="p-2">{r.currentState ?? '—'}</td>
                  <td className="p-2">
                    {r.found
                      ? <span className="text-green-600">gefunden</span>
                      : <span className="text-red-600">nicht zuordenbar</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {result.unmatched.length > 0 && (
            <div className="p-2 text-xs text-muted border-t border-line">
              {result.unmatched.length} Zeile(n) nicht zuordenbar — als Klärfall im Leiter-Journal sichtbar.
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ---------- ④ Abweichungen ----------

/** `*0`, `*` und leer sind im Alt-System PLATZHALTER fuer „Platz laut Alt-Liste leer" —
 *  KEINE Sternnummer 0 (Backend: isEmptyArtikel, migration-logic.ts). Roh angezeigt las
 *  sich `*0` wie ein echter Artikel und hat verwirrt (User 2026-07-17). */
function istAltLeer(raw: string | null | undefined): boolean {
  const s = (raw ?? '').trim()
  return s === '' || s === '*' || s === '*0'
}

function AbweichungenTab({
  scope,
  setScope,
}: {
  scope: 'lager1' | 'lager2' | 'all'
  setScope: (s: 'lager1' | 'lager2' | 'all') => void
}) {
  const abw = useAbweichungen()
  const allRows = abw.data ?? []

  const rows = useMemo(() => {
    if (scope === 'lager1') {
      return allRows.filter((r) => {
        const rc = r.placeBarcode.match(/^(R\d+)/i)?.[1] ?? ''
        return getLagerForRow(rc) === 1
      })
    }
    if (scope === 'lager2') {
      return allRows.filter((r) => {
        const rc = r.placeBarcode.match(/^(R\d+)/i)?.[1] ?? ''
        return getLagerForRow(rc) === 2
      })
    }
    return allRows
  }, [allRows, scope])

  return (
    <div className="space-y-4 pt-4">
      {/* Scope-Filter: Lager 1 vs. Lager 2 vs. Alle */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="inline-flex rounded-lg border border-line bg-panel p-1 text-xs">
          <button
            type="button"
            onClick={() => setScope('lager1')}
            className={'px-3 py-1.5 rounded-md font-medium transition-all ' + (scope === 'lager1' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted hover:text-foreground')}
          >
            Lager 1 (R01–R21)
          </button>
          <button
            type="button"
            onClick={() => setScope('lager2')}
            className={'px-3 py-1.5 rounded-md font-medium transition-all ' + (scope === 'lager2' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted hover:text-foreground')}
          >
            Lager 2 (R22–R32)
          </button>
          <button
            type="button"
            onClick={() => setScope('all')}
            className={'px-3 py-1.5 rounded-md font-medium transition-all ' + (scope === 'all' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted hover:text-foreground')}
          >
            Alle Regale
          </button>
        </div>
        <div className="text-xs text-muted font-mono">
          {rows.length} Abweichung(en) {scope === 'lager1' ? 'in Lager 1' : scope === 'lager2' ? 'in Lager 2' : 'insgesamt'}
        </div>
      </div>

      {scope === 'lager1' && (
        <div className="rounded-xl border border-blue-500/40 bg-blue-500/10 p-3 text-xs text-blue-950 dark:text-blue-200 flex items-start gap-2">
          <span className="text-base leading-none">ℹ️</span>
          <div>
            <strong>Lager 1 (R01–R21) offiziell abgeschlossen:</strong> Alle Bestände bis einschließlich Regal 21 sind im System erfasst.
            Verbliebene Abweichungen für Lager 1 werden ignoriert, da der Plattform-Bestand als verbindlicher Ist-Zustand gilt.
          </div>
        </div>
      )}

      <div className="overflow-x-auto rounded-xl border border-line">
        <table className="w-full text-sm">
          <thead className="bg-panel text-left">
            <tr>
              <th className="p-2">Platz</th><th className="p-2">Alt-CSV-Erwartung</th>
              <th className="p-2">INFO (Alt-System)</th><th className="p-2">Erledigt von</th><th className="p-2">Erledigt am</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.placeBarcode} className="border-t border-line">
                <td className="p-2 font-mono">{r.placeBarcode}</td>
                <td className="p-2">
                  {istAltLeer(r.expectedArtikelRaw)
                    ? <span className="text-muted italic">laut Alt-Liste leer</span>
                    : r.expectedArtikelRaw}
                </td>
                <td className="p-2 text-muted">{r.infoText ?? '—'}</td>
                <td className="p-2 text-muted">{r.erledigtByName ?? '—'}</td>
                <td className="p-2 text-muted">{r.erledigtAt ? new Date(r.erledigtAt).toLocaleString('de-DE') : '—'}</td>
              </tr>
            ))}
            {rows.length === 0 && !abw.isLoading && (
              <tr><td colSpan={5} className="p-4 text-center text-muted">Keine Abweichungen{scope !== 'all' ? ' in diesem Bereich' : ''}.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ---------- Digitale Regal-Kontrolle: klickbares Raster (User-Entscheid 2026-07-14) ----------
// Klick-Zyklus je leerem Platz: offen (weiß) → leer ✓ (grün) → belegt/Abweichung ⚠ (rot) → offen.
// Belegte Plätze (Sternnummer) + bereits erfasste sind grau und nicht klickbar.
function ShelfControlGrid({ rowCode, count, lead, onClose }: { rowCode: string; count: boolean; lead: boolean; onClose: () => void }) {
  const overview = useShelfOverview(rowCode)
  const actions = usePlaceActions()
  const g = overview.data
  // Kartons x Stueck + Rest-Editor (User-Wunsch 2026-07-15, Fix 2026-09-21): Doppelklick auf eine erfasste Zelle
  // oeffnet ein Inline-Panel — Eingabe wahlweise als Kartons + Stueck/Karton + Rest ODER als direkte Gesamtmenge.
  // Verhindert das Ueberschreiben von piecesPerCarton mit 1 bei geoeffneten Paletten mit ungeraden Stueckzahlen.
  const [edit, setEdit] = useState<{
    cell: ShelfCell
    mode: 'kartons' | 'direkt'
    kartons: string
    stueck: string
    rest: string
    direktQty: string
  } | null>(null)

  // RA6-WEB-4: Aktions-Gates folgen den Backend-Routen — leer/Abweichung melden = count,
  // Zuruecksetzen (place-reset) + Mengen-Korrektur (position/correct) = lead. Die ANSICHT
  // selbst ist read und fuer alle offen.
  function onCellClick(c: ShelfCell) {
    if (c.occupied === true || c.status === 'belegt' || c.sternnummer !== null || c.status === 'aufgenommen') return
    // R6-Nachschliff: NUR `count` autorisiert — `lead` impliziert im Backend KEIN count
    // (requireWarehouse('count') lehnt lead-only mit 403 ab; auth.ts:76 spiegelt das).
    if (c.status === 'offen') {
      if (!count) { toast.error('Leer melden erfordert Zähl-Berechtigung (count)'); return }
      actions.empty.mutate(c.placeBarcode)
    } else if (c.status === 'leer') {
      if (!count) { toast.error('Abweichung melden erfordert Zähl-Berechtigung (count)'); return }
      actions.discrepancy.mutate(c.placeBarcode)
    } else if (c.status === 'abweichung') {
      if (!lead) { toast.error('Zurücksetzen ist der Lagerleitung vorbehalten'); return }
      actions.reset.mutate(c.placeBarcode)
    }
  }

  // Doppelklick auf eine ERFASSTE (gruene) Zelle: Editor mit aktuellen Werten vorbefuellen.
  function onCellDoubleClick(c: ShelfCell) {
    if (!lead) return // Mengen-Korrektur (position/correct) ist lead-gated (Backend-Matrix)
    if (c.status !== 'aufgenommen' || c.positionId == null) return
    const ppc = c.piecesPerCarton ?? null
    const curQty = c.qty ?? 0
    let kStr = ''
    let rStr = '0'
    if (ppc && ppc > 0 && c.qty != null) {
      const k = Math.floor(curQty / ppc)
      const r = curQty % ppc
      kStr = String(k)
      rStr = String(r)
    }
    setEdit({
      cell: c,
      mode: 'kartons',
      kartons: kStr,
      stueck: ppc ? String(ppc) : '',
      rest: rStr,
      direktQty: c.qty != null ? String(c.qty) : '',
    })
  }

  function onEditSave() {
    if (!edit || edit.cell.positionId == null) return
    if (edit.mode === 'kartons') {
      const k = Number(edit.kartons.trim())
      const s = Number(edit.stueck.trim())
      const r = edit.rest.trim() === '' ? 0 : Number(edit.rest.trim())
      if (!Number.isInteger(k) || k < 0) {
        toast.error('Kartons: ganze Zahl ≥ 0'); return
      }
      if (!Number.isInteger(s) || s < 1) {
        toast.error('Stück/Karton: ganze Zahl ≥ 1'); return
      }
      if (!Number.isInteger(r) || r < 0) {
        toast.error('Rest / Einzelstücke: ganze Zahl ≥ 0'); return
      }
      const qty = k * s + r
      if (qty < 1) {
        toast.error('Gesamtmenge muss mindestens 1 Stück sein'); return
      }
      const reasonSuffix = r > 0 ? `${k}×${s}+${r}=${qty}` : `${k}×${s}=${qty}`
      actions.correctQty.mutate({
        positionId: edit.cell.positionId, qty, piecesPerCarton: s,
        // P1-Nachschliff: das Ziel-Feld waehlt der Server aus dem gesperrten Zustand —
        // der Raster-Zustand hier ist nur ein Schnappschuss (siehe buildCorrectQtyBody).
        reason: `Digitale Regal-Kontrolle: ${edit.cell.qty ?? '?'} → ${reasonSuffix} (${edit.cell.placeBarcode})`,
      }, {
        onSuccess: () => setEdit(null),
        // R6-Nachschliff (RA6-WEB-6): bei unklarem Transport-Ausgang den Editor SCHLIESSEN —
        // der Speichern-Button darf nicht offen bleiben (blinder Resend = zweite
        // correct_qty-Journalzeile). Das Raster refetcht (Hook-onError) und zeigt den
        // Ist-Wert; eine erneute Korrektur ist dann eine bewusste neue Handlung.
        onError: (e) => { if (isTransportError(e)) setEdit(null) },
      })
    } else {
      const d = Number(edit.direktQty.trim())
      if (!Number.isInteger(d) || d < 1) {
        toast.error('Gesamtmenge: ganze Zahl ≥ 1'); return
      }
      const qty = d
      // Bestehende Packungsgroesse erhalten (z.B. 100 St./Karton bei geöffneter Palette),
      // keinesfalls mit 1 ueberschreiben!
      const piecesPerCarton = edit.cell.piecesPerCarton ?? undefined
      actions.correctQty.mutate({
        positionId: edit.cell.positionId, qty,
        ...(piecesPerCarton ? { piecesPerCarton } : {}),
        reason: `Digitale Regal-Kontrolle: ${edit.cell.qty ?? '?'} → ${qty} St. direkt (${edit.cell.placeBarcode})`,
      }, {
        onSuccess: () => setEdit(null),
        onError: (e) => { if (isTransportError(e)) setEdit(null) },
      })
    }
  }

  function renderCell(c: ShelfCell | undefined) {
    if (!c) return <div className="w-11 h-8 bg-zinc-400/40" aria-hidden />   // kein Platz an dieser Position
    if (c.displayState === 'recapture_pending') return (
      <div className="w-11 min-h-8 border border-line bg-amber-100 text-amber-900 text-[9px]"
        title={`${c.placeBarcode} · ${c.sternnummer ?? ''} · ${c.paletteNo ?? ''} · Wartet auf erneute Aufnahme`}>
        <span>{c.sternnummer}</span><span className="block">{c.paletteNo}</span>
        <span className="block">Wartet auf erneute Aufnahme</span>
      </div>
    )
    const belegt = c.occupied === true || c.status === 'belegt' || c.sternnummer !== null
    const clickable = !belegt && c.status !== 'aufgenommen'
    let cls = 'w-11 h-8 flex items-center justify-center border border-line text-[9px] select-none '
    let label = ''
    // User-Wunsch 2026-07-15: bereits ERFASSTE Paletten (aufgenommen) leuchten GRUEN —
    // mit Sternnummer, damit sichtbar bleibt WAS dort erfasst wurde. Vorrang vor "belegt"
    // (eine erfasste Zelle hat weiterhin eine Sternnummer).
    if (c.status === 'aufgenommen') { cls += 'bg-green-600 text-white font-mono font-semibold'; label = belegt ? (c.sternnummer as string).slice(0, 6) : '✓' }
    else if (belegt) { cls += 'bg-zinc-300 dark:bg-zinc-700 font-mono'; label = c.sternnummer?.slice(0, 6) ?? 'Belegt' }
    else if (c.status === 'leer') { cls += 'bg-green-500/70 cursor-pointer'; label = '✓' }
    else if (c.status === 'abweichung') { cls += 'bg-red-500/70 cursor-pointer'; label = '⚠' }
    else { cls += 'bg-white text-zinc-800 cursor-pointer hover:bg-green-200' }  // offen
    const title = c.placeBarcode + (belegt ? ` · ${c.sternnummer ?? 'Belegt'}` : '')
      + (c.status === 'aufgenommen'
        ? ` · erfasst ✓ · Menge: ${c.qty ?? '?'} St. — Doppelklick = Menge korrigieren`
        : ` · ${c.status}`)
    // Codex 2026-07-14: KEIN globales busy-Gate mehr (verschluckte sonst schnelle Folgeklicks auf
    // andere Zellen waehrend einer laufenden Mutation). Klickbare Zelle = Button-Semantik (a11y).
    // Erfasste (gruene) Zellen: kein Einzelklick, aber Doppelklick = Mengen-Korrektur.
    if (c.status === 'aufgenommen') {
      return <div className={cls} title={title} onDoubleClick={() => onCellDoubleClick(c)}>{label}</div>
    }
    if (!clickable) return <div className={cls} title={title}>{label}</div>
    return (
      <div
        className={cls} title={title} role="button" tabIndex={0}
        onClick={() => onCellClick(c)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onCellClick(c) } }}
      >{label}</div>
    )
  }

  return (
    <div className="rounded-xl border border-line bg-panel p-3 space-y-2">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="font-semibold">Digitale Kontrolle · Regal <span className="font-mono">{rowCode}</span>
          {g && (() => {
            const erfasst = Object.values(g.cell).filter((c) => c.status === 'aufgenommen').length
            return <span className="text-sm text-muted"> · belegt {g.occupiedCount} · leer {g.leerCount} · <span className="text-green-700 dark:text-green-500 font-semibold">erfasst {erfasst}</span></span>
          })()}
        </div>
        <Button size="sm" variant="outline" onClick={onClose}>Schließen</Button>
      </div>
      <div className="text-xs text-muted">
        <span className="inline-block w-3 h-3 align-middle bg-green-600 mr-1" /> = Palette erfasst (Sternnummer, per Scan-App gebucht) —{' '}
        <span className="font-medium">Maus drauf = Menge · Doppelklick = Menge korrigieren</span> ·{' '}
        graue Zellen = belegt, noch nicht erfasst. Klick auf einen leeren Platz: weiß (offen) →{' '}
        <span className="text-green-700 font-medium">hellgrün = leer ✓</span> →{' '}
        <span className="text-red-700 font-medium">rot = belegt/Abweichung ⚠</span> → weiß.
      </div>
      {/* Inline-Editor Mengen-Korrektur (Kartons x Stueck + Rest ODER Direkt-Menge) */}
      {edit && (() => {
        const kNum = Number(edit.kartons.trim())
        const sNum = Number(edit.stueck.trim())
        const rNum = edit.rest.trim() === '' ? 0 : Number(edit.rest.trim())
        const totalCalculated =
          Number.isInteger(kNum) && kNum >= 0 && Number.isInteger(sNum) && sNum >= 1 && Number.isInteger(rNum) && rNum >= 0
            ? kNum * sNum + rNum
            : null

        return (
          <div className="rounded-lg border-2 border-green-600 p-3 space-y-3">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-sm font-semibold">
                Menge korrigieren · <span className="font-mono">{edit.cell.placeBarcode}</span>
                {edit.cell.sternnummer && <span className="font-mono"> · <GrundartikelLink code={edit.cell.sternnummer} /></span>}
                <span className="text-muted font-normal">
                  {' '}· aktuell {edit.cell.qty ?? '?'} St.
                  {edit.cell.piecesPerCarton ? ` (à ${edit.cell.piecesPerCarton}/Karton)` : ''}
                </span>
                {edit.cell.positionState && edit.cell.positionState !== 'verschlossen' && (
                  // P1: bei geoeffneter Palette wird der laufende Circa-Stand korrigiert, nicht
                  // die Erstaufnahme-Zaehlung — das gehoert sichtbar an die Maske.
                  <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs font-normal text-amber-900">
                    Palette geöffnet — korrigiert den laufenden Bestand
                  </span>
                )}
              </div>
              <div className="flex rounded-md border border-line p-0.5 bg-zinc-100 dark:bg-zinc-800 text-xs">
                <button
                  type="button"
                  className={`px-2 py-1 rounded font-medium transition-colors ${
                    edit.mode === 'kartons'
                      ? 'bg-green-600 text-white shadow-sm'
                      : 'text-muted hover:text-foreground'
                  }`}
                  onClick={() => setEdit({ ...edit, mode: 'kartons' })}
                >
                  Kartons × Stück + Rest
                </button>
                <button
                  type="button"
                  className={`px-2 py-1 rounded font-medium transition-colors ${
                    edit.mode === 'direkt'
                      ? 'bg-green-600 text-white shadow-sm'
                      : 'text-muted hover:text-foreground'
                  }`}
                  onClick={() => setEdit({ ...edit, mode: 'direkt' })}
                >
                  Direkte Gesamtmenge
                </button>
              </div>
            </div>

            {edit.mode === 'kartons' ? (
              <div className="flex items-end gap-2 flex-wrap">
                <div className="flex flex-col gap-1">
                  <label className="text-[11px] text-muted font-medium">Kartons</label>
                  <input
                    type="number"
                    min={0}
                    value={edit.kartons}
                    autoFocus
                    placeholder="0"
                    className="w-20 rounded border border-line bg-transparent px-2 py-1.5 text-sm"
                    onChange={(e) => setEdit({ ...edit, kartons: e.target.value })}
                    onKeyDown={(e) => { if (e.key === 'Enter') onEditSave() }}
                  />
                </div>
                <span className="text-muted pb-1.5 font-bold">×</span>
                <div className="flex flex-col gap-1">
                  <label className="text-[11px] text-muted font-medium">Stück/Karton</label>
                  <input
                    type="number"
                    min={1}
                    value={edit.stueck}
                    placeholder="Stück"
                    className="w-24 rounded border border-line bg-transparent px-2 py-1.5 text-sm"
                    onChange={(e) => setEdit({ ...edit, stueck: e.target.value })}
                    onKeyDown={(e) => { if (e.key === 'Enter') onEditSave() }}
                  />
                </div>
                <span className="text-muted pb-1.5 font-bold">+</span>
                <div className="flex flex-col gap-1">
                  <label className="text-[11px] text-muted font-medium">Rest / Einzeln</label>
                  <input
                    type="number"
                    min={0}
                    value={edit.rest}
                    placeholder="0"
                    className="w-24 rounded border border-line bg-transparent px-2 py-1.5 text-sm"
                    onChange={(e) => setEdit({ ...edit, rest: e.target.value })}
                    onKeyDown={(e) => { if (e.key === 'Enter') onEditSave() }}
                  />
                </div>
                <div className="pb-1.5 text-sm">
                  = <span className="font-bold text-base">{totalCalculated != null && totalCalculated > 0 ? totalCalculated : '—'}</span> St.
                </div>
                <div className="flex items-center gap-2 ml-auto pb-0.5">
                  <Button size="sm" disabled={actions.correctQty.isPending} onClick={onEditSave}>
                    Speichern
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setEdit(null)}>
                    Abbrechen
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-end gap-2 flex-wrap">
                <div className="flex flex-col gap-1">
                  <label className="text-[11px] text-muted font-medium">Gesamtmenge (Stück)</label>
                  <input
                    type="number"
                    min={1}
                    value={edit.direktQty}
                    autoFocus
                    placeholder="z.B. 550"
                    className="w-32 rounded border border-line bg-transparent px-2 py-1.5 text-sm font-semibold"
                    onChange={(e) => setEdit({ ...edit, direktQty: e.target.value })}
                    onKeyDown={(e) => { if (e.key === 'Enter') onEditSave() }}
                  />
                </div>
                <span className="text-xs text-muted pb-2">
                  {edit.cell.piecesPerCarton
                    ? `Packungsgröße (${edit.cell.piecesPerCarton} St./Karton) bleibt unverändert erhalten.`
                    : 'Keine Packungsgröße hinterlegt.'}
                </span>
                <div className="flex items-center gap-2 ml-auto pb-0.5">
                  <Button size="sm" disabled={actions.correctQty.isPending} onClick={onEditSave}>
                    Speichern
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setEdit(null)}>
                    Abbrechen
                  </Button>
                </div>
              </div>
            )}
          </div>
        )
      })()}
      {overview.isLoading && <div className="text-sm text-muted">lädt…</div>}
      {overview.isError && (
        <div className="text-sm text-red-600 flex items-center gap-2">
          Raster konnte nicht geladen werden.
          <Button size="sm" variant="outline" onClick={() => void overview.refetch()}>Erneut versuchen</Button>
        </div>
      )}
      {g && g.columns.length === 0 && <div className="text-sm text-muted">Keine Plätze in diesem Regal.</div>}
      {g && g.columns.length > 0 && (
        <RegalFront<ShelfCell>
          columns={g.columns.map((c) => ({ field: c.fieldNo, slot: c.slotNo, label: c.label }))}
          levels={g.levels}
          cellAt={(p) => g.cell[`${p.field}:${p.slot}:${p.level}`]}
          renderCell={renderCell}
        />
      )}
    </div>
  )
}
